import type { ReactNode } from 'react';
import CellTowerOutlined from '@mui/icons-material/CellTowerOutlined';
import SmartphoneOutlined from '@mui/icons-material/SmartphoneOutlined';
import FormatListBulletedOutlined from '@mui/icons-material/FormatListBulletedOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import ToggleOnOutlined from '@mui/icons-material/ToggleOnOutlined';
import SpeedOutlined from '@mui/icons-material/SpeedOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import PaletteOutlined from '@mui/icons-material/PaletteOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import MonitorHeartOutlined from '@mui/icons-material/MonitorHeartOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

/**
 * Descriptor for one Settings sub-nav item. `labelKey` resolves against the
 * shared `common` namespace (`settingsNav.items.*`); `to` is the absolute
 * route; `icon` is the rail glyph. Slice 04 lands the Catalog group's
 * Carriers/Devices/Plans as real screens; every other item routes to the shared
 * `StubPage` so the nav is complete and slices 05–08 just fill their section.
 */
export interface SettingsNavItemDescriptor {
  key: string;
  labelKey: string;
  to: string;
  icon: ReactNode;
  badge?: number;
}

export interface SettingsNavGroupDescriptor {
  key: string;
  titleKey: string;
  items: SettingsNavItemDescriptor[];
}

/**
 * The grouped Settings navigation, mirroring the prototype `SETTINGS_GROUPS`
 * (`appshell/shell-data.js`): Catalog / Budgets / Notifications / Data. This is
 * the single source of truth for the two-pane {@link SettingsLayout} rail and
 * the route table in `routes.tsx`.
 */
export const SETTINGS_NAV_GROUPS: SettingsNavGroupDescriptor[] = [
  {
    // Portal-level settings for the customer's own organization. Kept first and
    // separate from Notifications so "Branding" (the portal) reads distinctly
    // from Notifications › "Branding & sender" (outbound email).
    key: 'organization',
    titleKey: 'settingsNav.groups.organization',
    items: [
      {
        key: 'organizationBranding',
        labelKey: 'settingsNav.items.organizationBranding',
        to: '/settings/branding',
        icon: <PaletteOutlined fontSize="inherit" />,
      },
    ],
  },
  {
    key: 'catalog',
    titleKey: 'settingsNav.groups.catalog',
    items: [
      {
        key: 'carriers',
        labelKey: 'settingsNav.items.carriers',
        to: '/settings/catalog/carriers',
        icon: <CellTowerOutlined fontSize="inherit" />,
      },
      {
        key: 'devices',
        labelKey: 'settingsNav.items.devices',
        to: '/settings/catalog/devices',
        icon: <SmartphoneOutlined fontSize="inherit" />,
      },
      {
        key: 'plans',
        labelKey: 'settingsNav.items.plans',
        to: '/settings/catalog/plans',
        icon: <FormatListBulletedOutlined fontSize="inherit" />,
      },
      {
        key: 'features',
        labelKey: 'settingsNav.items.features',
        to: '/settings/catalog/features',
        icon: <ToggleOnOutlined fontSize="inherit" />,
      },
      {
        key: 'packages',
        labelKey: 'settingsNav.items.packages',
        to: '/settings/catalog/packages',
        icon: <Inventory2Outlined fontSize="inherit" />,
      },
      {
        key: 'assignmentRules',
        labelKey: 'settingsNav.items.assignmentRules',
        to: '/settings/catalog/assignment-rules',
        icon: <AccountTreeOutlined fontSize="inherit" />,
      },
    ],
  },
  {
    key: 'budgets',
    titleKey: 'settingsNav.groups.budgets',
    items: [
      {
        key: 'budgetProfiles',
        labelKey: 'settingsNav.items.budgetProfiles',
        to: '/settings/budgets/profiles',
        icon: <SpeedOutlined fontSize="inherit" />,
      },
      {
        key: 'budgetAssignmentRules',
        labelKey: 'settingsNav.items.budgetAssignmentRules',
        to: '/settings/budgets/assignment-rules',
        icon: <AccountTreeOutlined fontSize="inherit" />,
      },
      {
        key: 'budgetOverrides',
        labelKey: 'settingsNav.items.budgetOverrides',
        to: '/settings/budgets/overrides',
        icon: <TuneOutlined fontSize="inherit" />,
      },
    ],
  },
  {
    key: 'notifications',
    titleKey: 'settingsNav.groups.notifications',
    items: [
      {
        key: 'templates',
        labelKey: 'settingsNav.items.templates',
        to: '/settings/notifications/templates',
        icon: <DescriptionOutlined fontSize="inherit" />,
      },
      {
        key: 'branding',
        labelKey: 'settingsNav.items.branding',
        to: '/settings/notifications/branding',
        icon: <PaletteOutlined fontSize="inherit" />,
      },
      {
        key: 'delivery',
        labelKey: 'settingsNav.items.delivery',
        to: '/settings/notifications/delivery',
        icon: <SendOutlined fontSize="inherit" />,
      },
    ],
  },
  {
    key: 'data',
    titleKey: 'settingsNav.groups.data',
    items: [
      {
        key: 'feedStatus',
        labelKey: 'settingsNav.items.feedStatus',
        to: '/settings/data/feed-status',
        icon: <MonitorHeartOutlined fontSize="inherit" />,
      },
      {
        key: 'carrierAccounts',
        labelKey: 'settingsNav.items.carrierAccounts',
        to: '/settings/data/accounts',
        icon: <BusinessOutlined fontSize="inherit" />,
      },
      {
        key: 'importWarnings',
        labelKey: 'settingsNav.items.importWarnings',
        to: '/settings/data/warnings',
        icon: <WarningAmberOutlined fontSize="inherit" />,
      },
    ],
  },
];
